import React, { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import moment, { Moment } from "moment";
import styled from "styled-components";
import { warningNotification } from "@shared/store/notification.duck";
import { getWhereaboutsReport, toExcelDownload } from '@features/reporting/services/ReportingService';
import { DateInputField } from '@shared/ui/molecules/DateInputField';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import FullScreenDateRangeSelector from '@shared/ui/molecules/calendar/FullScreenDateRangeSelector';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { selectCurrentUser } from "@shared/store/auth.duck";
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function WhereaboutsBookingDownloadReportBar(props: Props) {
  const {selectedTeamId, selectedTeamName, selectedDepartmentId,
    selectedDepartmentName, selectedWorkGroupName, selectedWorkGroupId, allDepartments, allWorkGroups} = props;
  const dispatch = useDispatch();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [fromDate, setFromDate] = useState<Moment | null>(null);
  const [toDate, setToDate] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false);
  const [hideUnknownWhereabouts, setHideUnknownWhereabouts] = useState(true);
  const current = useSelector(selectCurrentUser);

  const onDatesSelected = (startDate?: Moment, endDate?: Moment) => {
    if (startDate) setFromDate(startDate);
    if (endDate) setToDate(endDate);
  }

  const onClick = () => {
    if (!fromDate) {
      dispatch(warningNotification("Start date required."));
      return;
    }
    if (!toDate) {
      dispatch(warningNotification("End date required."));
      return;
    }
    if (!selectedTeamId && !selectedDepartmentId && !selectedWorkGroupId && !allDepartments && !allWorkGroups) {
      dispatch(warningNotification("Select a group."));
      return;
    }
    if (!current) {
      dispatch(warningNotification("Current user not found."));
      return;
    }

    setLoading(true);
    getWhereaboutsReport(current.companyEntity.id, fromDate, toDate, undefined, selectedTeamId, selectedDepartmentId, selectedWorkGroupId, hideUnknownWhereabouts, allDepartments, allWorkGroups)
      .then(response => toExcelDownload(response, 'Whereabouts report'))
      .finally(() => setLoading(false));
  }

  return (<>
      <BodyRegular weight={600}>Download whereabouts booking report</BodyRegular>
      <ReportRowContainer>
        <BodyRegular style={{paddingBottom: 8}}>{selectedTeamName || selectedDepartmentName || selectedWorkGroupName}</BodyRegular>

        <DateInputField onClick={() => setIsCalendarOpen(true)}
                        placeholder="textinput.add-date"
                        value={fromDate ? fromDate.format('D MMMM YYYY') : ''}
                        label="textinput.from"
                        editableOnly={true}/>

        <DateInputField onClick={() => setIsCalendarOpen(true)}
                        placeholder="textinput.add-date"
                        value={toDate ? toDate.format('D MMMM YYYY') : ''}
                        label="textinput.to"
                        editableOnly={true}/>

        <OutlineButton size={ButtonSize.Medium}
                       text="button.download-excel"
                       disabled={loading}
                       click={onClick} />

        <Checkbox value={hideUnknownWhereabouts}
                  className={'downloadReportBar__checkbox'}
                  onChange={setHideUnknownWhereabouts}
                  text={"Hide unknown whereabouts"} />
      </ReportRowContainer>
      <FullScreenDateRangeSelector open={isCalendarOpen}
                                   onDateSelect={(startDate, endDate) => onDatesSelected(startDate, endDate)}
                                   onClose={() => setIsCalendarOpen(false)}
                                   startDate={fromDate} endDate={toDate}
                                   initialDate={fromDate ?? moment()}/>
    </>
  )
}

interface Props {
  selectedTeamId?: string;
  selectedTeamName?: string;

  selectedDepartmentId?: number;
  selectedDepartmentName?: string;

  selectedWorkGroupId?: number;
  selectedWorkGroupName?: string;

  allDepartments?: boolean;
  allWorkGroups?: boolean;
}

const ReportRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media (${tablet}) {
    flex-direction: row;
    align-items: flex-end;
    flex: 1;
    .textField {
      margin-bottom: 0;
    }
    .teamDropDownList {
      margin-bottom: 0;
    }
    > * {
      width: 20%;
      padding-right: 24px;
    }
    .downloadReportBar__checkbox {
      margin: 0 0 16px auto;
    }
  }
`
